import { MouseEvent } from "react";

import PhoneIcon from "@mui/icons-material/Phone";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import type { SxProps } from "@mui/material/styles";

import { success } from "app/themes/colors";
import usePracticeVoiceEnabled from "hooks/use-practice-voice-enabled.hook";
import { Patient } from "services/types/patients.type";
import { formatPhoneNumber, isNotBlank } from "utils/common";

import useMakeCall, { useDialDeviceTooltip } from "./use-make-call.hook";

type CallPatientIconProps = {
  phoneNumber?: string | null;
  patient?: Partial<Patient>;
  size?: "small" | "medium";
  // For surfaces where the surrounding row/option is itself clickable
  // (search results, table rows) so dialing doesn't also trigger the row.
  stopPropagation?: boolean;
  sx?: SxProps;
};

// Compact icon-only click-to-call for surfaces that already render the phone
// number themselves. Renders nothing when the practice has no voice
// subscription or no phone resolves, so call sites can drop it in unconditionally. Phrasing content
// (button + svg), so it is safe inside Typography.
export default function CallPatientIcon({
  phoneNumber,
  patient,
  size = "small",
  stopPropagation,
  sx
}: CallPatientIconProps) {
  const isPracticeVoiceEnabled = usePracticeVoiceEnabled();
  const makeCall = useMakeCall();

  const phone = [phoneNumber, patient?.phone, patient?.secondary_phone]
    .filter(isNotBlank)
    ?.at(0);
  const tooltip = useDialDeviceTooltip(
    `Call ${patient?.fullName || ""} ${formatPhoneNumber(phone ?? "")}`
  );

  if (!isPracticeVoiceEnabled || !phone) return null;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (stopPropagation) event.stopPropagation();
    makeCall(phone, patient, event);
  };

  // Stopping mousedown keeps the row from selecting, but it also skips the
  // host's own mousedown handler — in the patient search that is MUI
  // Autocomplete's, which prevents the default so the input keeps focus. Do
  // that here too: otherwise the button takes focus, the input blurs, the
  // popover closes, and the click never arrives.
  const handleMouseDown = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <Tooltip disableInteractive title={tooltip}>
      <IconButton
        aria-label={`Call ${formatPhoneNumber(phone)}`}
        size={size}
        onClick={handleClick}
        onMouseDown={stopPropagation ? handleMouseDown : undefined}
        sx={{ color: success[600], p: 0.5, ...sx }}
      >
        <PhoneIcon sx={{ fontSize: size === "small" ? 18 : 22 }} />
      </IconButton>
    </Tooltip>
  );
}
